import React from 'react';
import { TextField, MenuItem, Typography, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import { Clear } from '@mui/icons-material';

interface NextStepDropdownProps {
  selectedOption: string;
  setSelectedOption: (value: string) => void;
  dropdownOptions: Array<{ label: string; value: string }>;
  label: string;
  placeholder: string;
  required?: boolean;
  onHandleSave?: () => void;
  onClearAndSave?: (fieldName: string) => void;
  fieldName?: string;
  showError?: boolean;
  onClearError?: () => void;
  isLoading?: boolean;
}

interface ClearButtonProps {
  onClear: () => void;
  onClearAndSave?: (fieldName: string) => void;
  fieldName?: string;
  useIconButton?: boolean;
}

export const NextStepDropdown: React.FC<NextStepDropdownProps> = ({
  selectedOption,
  setSelectedOption,
  dropdownOptions,
  label,
  placeholder,
  required = false,
  onHandleSave,
  onClearAndSave,
  fieldName,
  showError = false,
  onClearError,
  isLoading = false,
}) => {
  return (
    <TextField
      select
      label={label}
      value={selectedOption}
      size="small"
      onChange={(e) => {
        setSelectedOption(e.target.value);
        if (onClearError) onClearError();
      }}
      onBlur={onHandleSave}
      sx={{ marginBottom: '16px' }}
      required={required}
      error={showError}
      helperText={showError ? 'This field is required' : ''}
      InputLabelProps={{ shrink: true, sx: { color: 'text.secondary' } }}
      InputProps={{
        endAdornment: selectedOption ? (
          <ClearButton
            onClear={() => setSelectedOption('')}
            onClearAndSave={onClearAndSave}
            fieldName={fieldName}
            useIconButton={false}
          />
        ) : null,
      }}
      inputProps={{ style: { fontSize: 16 } }}
      SelectProps={{
        displayEmpty: true,
        renderValue: (selected) => {
          if (!selected) {
            return (
              <Typography sx={{ color: showError ? 'error.main' : 'text.secondary', fontSize: 16 }}>
                {placeholder}
              </Typography>
            );
          }
          return selected as React.ReactNode;
        },
      }}>
      {isLoading ? (
        <MenuItem disabled sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={20} />
        </MenuItem>
      ) : (
        dropdownOptions.map((option: any) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))
      )}
    </TextField>
  );
};

export const ClearButton: React.FC<ClearButtonProps> = ({
  onClear,
  onClearAndSave,
  fieldName,
  useIconButton = true,
}) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevents blur event on the input field
    e.stopPropagation();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear();
    if (onClearAndSave && fieldName) {
      onClearAndSave(fieldName);
    }
  };

  return (
    <InputAdornment position="start" sx={{ paddingRight: '8px' }}>
      {useIconButton ? (
        <IconButton size="small" onMouseDown={handleMouseDown} onClick={handleClick}>
          <Clear fontSize="small" />
        </IconButton>
      ) : (
        <Clear
          fontSize="small"
          onMouseDown={handleMouseDown}
          onClick={handleClick}
          sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
        />
      )}
    </InputAdornment>
  );
};
